import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, KeyRound, Info, ArrowLeft, ArrowRight, User, Lock } from 'lucide-react';
import { TWCheckbox } from '../ui/TWCheckbox';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api/apiClient';
import { PasskeyService } from '../../services/auth/passkeyService';
import toast from '../../utils/toast';
import { getImageUrl } from '../../utils/imageUtils';

interface LoginFormTWProps {
  onSwitchToRegister?: () => void;
  onClose?: () => void;
  onLoginSuccess?: () => void;
}

interface LoginData {
  emailOrUsername: string;
  password: string;
  rememberMe: boolean;
}

type LoginStep = 'identifier' | 'credentials';

export const LoginFormTW: React.FC<LoginFormTWProps> = ({
  onSwitchToRegister,
  onClose,
  onLoginSuccess,
}) => {
  const { login, loginWithToken, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  // Form State
  const [formData, setFormData] = useState<LoginData>({
    emailOrUsername: '',
    password: '',
    rememberMe: false
  });

  // UI State
  const [step, setStep] = useState<LoginStep>('identifier');
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [isErrorFading, setIsErrorFading] = useState(false);

  // Passkey State
  const [isPasskeySupported, setIsPasskeySupported] = useState(false);
  const [userHasPasskey, setUserHasPasskey] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [showPasswordFallback, setShowPasswordFallback] = useState(false);
  const [passkeyCapabilities, setPasskeyCapabilities] = useState<any>(null);

  // Auto-hide error messages after 5 seconds
  useEffect(() => {
    if (formErrors.general || error) {
      setIsErrorFading(false);

      const fadeTimer = setTimeout(() => {
        setIsErrorFading(true);
      }, 4500);

      const removeTimer = setTimeout(() => {
        setFormErrors(prev => ({ ...prev, general: '' }));
        if (error) clearError();
        setIsErrorFading(false);
      }, 5000);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(removeTimer);
      };
    }
  }, [formErrors.general, error, clearError]);

  // Auto-focus first field
  useEffect(() => {
    // Fokus-Funktion mit mehreren Versuchen für mobile Browser
    const focusInput = () => {
      if (step === 'identifier') {
        emailInputRef.current?.focus();
      } else if (step === 'credentials' && (showPasswordFallback || !userHasPasskey || !isPasskeySupported)) {
        passwordInputRef.current?.focus();
      }
    };

    // Erster Versuch sofort
    focusInput();

    // Zweiter Versuch nach kurzem Delay (für Desktop)
    const timer1 = setTimeout(focusInput, 100);

    // Dritter Versuch nach längerem Delay (für Mobile nach Animation)
    const timer2 = setTimeout(focusInput, 300);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [step, showPasswordFallback, userHasPasskey, isPasskeySupported]);

  // Check Passkey Support
  useEffect(() => {
    const checkPasskeySupport = async () => {
      const capabilities = await PasskeyService.getCapabilities();
      setPasskeyCapabilities(capabilities);
      setIsPasskeySupported(capabilities.supported && capabilities.platformAuthenticator);

      if (capabilities.supported) {
        PasskeyService.startConditionalAuthentication(
          async (result) => {
            const loginResult = await loginWithToken(result.token);
            if (!loginResult.success) return;

            if (onLoginSuccess) {
              onLoginSuccess();
            } else {
              window.location.href = '/dashboard';
            }
          },
          () => {}
        );
      }
    };

    checkPasskeySupport();

    return () => {
      PasskeyService.abortConditionalAuthentication();
    };
  }, [onLoginSuccess, loginWithToken]);

  const validateIdentifier = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.emailOrUsername.trim()) {
      errors.emailOrUsername = 'E-Mail oder Benutzername ist erforderlich';
    } else if (formData.emailOrUsername.trim().length < 3) {
      errors.emailOrUsername = 'Mindestens 3 Zeichen erforderlich';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePassword = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.password) {
      errors.password = 'Passwort ist erforderlich';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setFormErrors({});

    if (!validateIdentifier()) return;

    setIsCheckingUser(true);

    try {
      if (isPasskeySupported) {
        const result = await PasskeyService.checkUserHasPasskey(formData.emailOrUsername.trim());
        setUserHasPasskey(result.hasPasskey);

        if (result.hasPasskey) {
          setStep('credentials');
          setShowPasswordFallback(false);
          setTimeout(() => {
            handlePasskeyLogin();
          }, 300);
          return;
        }
      }

      setStep('credentials');
      setShowPasswordFallback(true);
    } catch (error) {
      console.error('Error checking user:', error);
      setStep('credentials');
      setShowPasswordFallback(true);
    } finally {
      setIsCheckingUser(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validatePassword()) return;

    try {
      const result = await login(formData);

      if (result.success) {
        if (onLoginSuccess) {
          onLoginSuccess();
        } else if (onClose) {
          onClose();
        }
      }
    } catch (loginError: any) {
      if (loginError?.response?.data?.action === 'resend_verification') {
        setShowResendButton(true);
        setResendEmail(loginError.response.data.email);
        setFormErrors({
          general: loginError.response.data.message + ' Sie können die E-Mail-Bestätigung erneut anfordern.'
        });
      } else {
        setFormErrors({ general: loginError?.response?.data?.message || 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Anmeldedaten.' });
      }
    }
  };

  const handlePasskeyLogin = useCallback(async () => {
    setIsPasskeyLoading(true);
    clearError();
    setFormErrors({});

    try {
      const email = formData.emailOrUsername.includes('@')
        ? formData.emailOrUsername.trim()
        : undefined;
      const result = await PasskeyService.loginWithPasskey(email);

      const loginResult = await loginWithToken(result.token);
      if (!loginResult.success) {
        setFormErrors({ general: 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.' });
        setShowPasswordFallback(true);
        return;
      }

      toast.success('Mit Passkey angemeldet!');

      if (onLoginSuccess) {
        onLoginSuccess();
      } else {
        window.location.href = '/dashboard';
      }
    } catch (error: any) {
      console.error('Passkey login error:', error);
      setFormErrors({ general: error.message || 'Passkey-Login fehlgeschlagen' });
      setShowPasswordFallback(true);
    } finally {
      setIsPasskeyLoading(false);
    }
  }, [formData.emailOrUsername, clearError, onLoginSuccess, loginWithToken]);

  const handleResendVerification = async () => {
    if (!resendEmail) return;

    setResendLoading(true);
    try {
      const result = await apiClient.resendVerification(resendEmail);

      if (result.success) {
        toast.success(result.data?.message || 'Bestätigungs-E-Mail wurde erneut gesendet!');
        setShowResendButton(false);
        setFormErrors({});
      } else {
        toast.error(result.error || 'Fehler beim Senden der E-Mail');
      }
    } catch (error) {
      console.error('Resend error:', error);
      toast.error('Fehler beim Senden der E-Mail. Bitte versuchen Sie es erneut.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (formErrors.general) {
      setFormErrors(prev => ({ ...prev, general: '' }));
    }
    if (error) {
      clearError();
    }
  };

  const handleBack = () => {
    setStep('identifier');
    setFormErrors({});
    setShowPasswordFallback(false);
    setUserHasPasskey(false);
    clearError();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && step === 'identifier') {
      e.preventDefault();
      handleContinue(e as any);
    }
  };

  const isIdentifierValid = formData.emailOrUsername.trim().length >= 3;
  const isPasswordValid = formData.password.length > 0;

  // Logo-URL - zentrale Funktion für korrekte Umgebungserkennung
  const logoUrl = getImageUrl('/images/branding/renditefuchs-logo.svg');

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8 bg-gray-100 dark:bg-gray-950">
      <div className="w-full max-w-sm space-y-10">
        {/* Logo & Header */}
        <div>
          <Link to="/" aria-label="Zur Startseite" className="block">
            <img
              alt="Renditefuchs"
              src={logoUrl}
              className="mx-auto h-10 w-auto"
            />
          </Link>
          <h2 className="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white">
            Anmelden
          </h2>
        </div>

        {/* Step 1: Identifier Input */}
        {step === 'identifier' && (
          <form onSubmit={handleContinue} className="space-y-4" autoComplete="on" noValidate>
            <div>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <User className="h-5 w-5 text-gray-500" aria-hidden="true" />
                </div>
                <input
                  ref={emailInputRef}
                  id="emailOrUsername"
                  name="username"
                  type="text"
                  autoFocus
                  autoComplete="username webauthn"
                  placeholder="E-Mail oder Benutzername"
                  value={formData.emailOrUsername}
                  onChange={(e) => handleInputChange('emailOrUsername', e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isCheckingUser}
                  tabIndex={1}
                  className={`block w-full rounded-md bg-gray-100 dark:bg-white/5 pl-10 pr-3 py-1.5 text-base text-gray-900 dark:text-white outline-1 -outline-offset-1 placeholder:text-gray-500 sm:text-sm/6 disabled:opacity-50 disabled:cursor-not-allowed antialiased font-normal ${
                    formErrors.emailOrUsername
                      ? 'outline-red-500 focus:outline-red-500'
                      : 'outline-gray-300 dark:outline-white/10 focus:outline-2 focus:-outline-offset-2 focus:outline-brand'
                  }`}
                />
              </div>
              {formErrors.emailOrUsername && (
                <p className="mt-2 text-sm text-red-400">{formErrors.emailOrUsername}</p>
              )}
            </div>

            {/* Continue Button */}
            <button
              type="submit"
              disabled={isCheckingUser || !isIdentifierValid}
              tabIndex={2}
              className="flex w-full justify-center items-center gap-2 rounded-md bg-brand px-3 py-2.5 text-sm/6 font-semibold text-gray-900 hover:bg-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCheckingUser ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Wird geprüft...</span>
                </>
              ) : (
                <>
                  <span>Weiter</span>
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>

            {/* Register Link */}
            <p className="mt-10 text-center text-sm/6 text-gray-500 dark:text-gray-400">
              Noch kein Konto?{' '}
              {onSwitchToRegister ? (
                <button
                  type="button"
                  onClick={onSwitchToRegister}
                  disabled={isCheckingUser}
                  tabIndex={3}
                  className="text-brand hover:text-brand-hover transition-colors"
                >
                  Jetzt registrieren
                </button>
              ) : (
                <Link
                  to="/register"
                  tabIndex={3}
                  className="text-brand hover:text-brand-hover transition-colors"
                >
                  Jetzt registrieren
                </Link>
              )}
            </p>
          </form>
        )}

        {/* Step 2: Credentials (Passkey or Password) */}
        {step === 'credentials' && (
          <div className="space-y-6">
            {/* User identifier display */}
            <div className="flex items-center gap-3 p-4 rounded-lg bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-800">
                <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </div>
              <p className="flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">
                {formData.emailOrUsername}
              </p>
              <button
                type="button"
                onClick={handleBack}
                disabled={isLoading || isPasskeyLoading}
                tabIndex={1}
                className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-brand transition-colors disabled:opacity-50"
              >
                <ArrowLeft className="h-3 w-3" />
                Ändern
              </button>
            </div>

            {/* Passkey Authentication */}
            {userHasPasskey && isPasskeySupported && !showPasswordFallback && (
              <div className="space-y-4">
                {isPasskeyLoading ? (
                  <div className="flex flex-col items-center justify-center py-8 rounded-lg bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10">
                    <Loader2 className="h-8 w-8 text-brand animate-spin mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Warte auf {passkeyCapabilities?.authenticatorType || 'Authentifizierung'}...
                    </p>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handlePasskeyLogin}
                    disabled={isPasskeyLoading || isLoading}
                    tabIndex={2}
                    className="flex w-full justify-center items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm/6 font-semibold text-gray-900 hover:bg-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50 transition-colors"
                  >
                    <KeyRound className="h-5 w-5" />
                    <span>Mit {passkeyCapabilities?.authenticatorType || 'Passkey'} anmelden</span>
                  </button>
                )}

                {/* Error Message - Passkey */}
                {(formErrors.general || error) && (
                  <div className={`p-4 rounded-lg bg-status-error/10 border border-red-500/50 transition-opacity duration-500 ${isErrorFading ? 'opacity-0' : 'opacity-100'}`}>
                    <p className="text-sm text-red-400">{formErrors.general || error}</p>
                  </div>
                )}

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowPasswordFallback(true)}
                    tabIndex={3}
                    className="text-sm font-semibold text-brand hover:text-brand-hover transition-colors"
                  >
                    Stattdessen mit Passwort anmelden
                  </button>
                </div>
              </div>
            )}

            {/* Password Form */}
            {(showPasswordFallback || !userHasPasskey || !isPasskeySupported) && (
              <form onSubmit={handlePasswordSubmit} className="space-y-4" autoComplete="on" noValidate>
                <div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className="block text-sm/6 font-medium text-gray-800 dark:text-gray-100">
                      Passwort
                    </label>
                    <div className="text-sm">
                      <button
                        type="button"
                        onClick={() => navigate('/auth/forgot-password')}
                        disabled={isLoading}
                        tabIndex={-1}
                        className="font-semibold text-brand hover:text-brand-hover transition-colors"
                      >
                        Passwort vergessen?
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Lock className="h-5 w-5 text-gray-500" aria-hidden="true" />
                    </div>
                    <input
                      ref={passwordInputRef}
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Ihr Passwort"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      disabled={isLoading}
                      tabIndex={2}
                      className={`block w-full rounded-md bg-gray-100 dark:bg-white/5 pl-10 pr-10 py-1.5 text-base text-gray-900 dark:text-white outline-1 -outline-offset-1 placeholder:text-gray-500 sm:text-sm/6 disabled:opacity-50 disabled:cursor-not-allowed antialiased font-normal ${
                        formErrors.password
                          ? 'outline-red-500 focus:outline-red-500'
                          : 'outline-gray-300 dark:outline-white/10 focus:outline-2 focus:-outline-offset-2 focus:outline-brand'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                      tabIndex={-1}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {formErrors.password && (
                    <p className="mt-2 text-sm text-red-400">{formErrors.password}</p>
                  )}
                </div>

                {/* Remember Me */}
                <div className="flex items-center justify-between">
                  <TWCheckbox
                    id="rememberMe"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                    disabled={isLoading}
                    tabIndex={3}
                    label={
                      <span className="flex items-center gap-2">
                        Angemeldet bleiben
                        <span className="group/tooltip relative">
                          <Info className="h-4 w-4 text-gray-500 cursor-help" />
                          <span className="invisible group-hover/tooltip:visible absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 text-xs text-center rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-white/10 shadow-lg z-50">
                            Du bleibst für 30 Tage angemeldet und musst dich nicht erneut einloggen
                          </span>
                        </span>
                      </span>
                    }
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading || isPasskeyLoading || !isPasswordValid}
                  tabIndex={4}
                  className="flex w-full justify-center items-center gap-2 rounded-md bg-brand px-3 py-2.5 text-sm/6 font-semibold text-gray-900 hover:bg-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Wird angemeldet...</span>
                    </>
                  ) : (
                    <span>Anmelden</span>
                  )}
                </button>

                {/* Error Message - Password */}
                {(formErrors.general || error) && (
                  <div className={`p-4 rounded-lg bg-status-error/10 border border-red-500/50 transition-opacity duration-500 ${isErrorFading ? 'opacity-0' : 'opacity-100'}`}>
                    <p className="text-sm text-red-400">{formErrors.general || error}</p>
                    {showResendButton && (
                      <div className="mt-3 pt-3 border-t border-red-500/30">
                        <button
                          type="button"
                          onClick={handleResendVerification}
                          disabled={resendLoading}
                          className="flex w-full justify-center items-center gap-2 rounded-md bg-gray-200 dark:bg-gray-800 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-white/10 hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                        >
                          {resendLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Wird gesendet...
                            </>
                          ) : (
                            'E-Mail erneut senden'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Switch back to passkey */}
                {userHasPasskey && isPasskeySupported && showPasswordFallback && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordFallback(false);
                        setFormErrors({});
                        handlePasskeyLogin();
                      }}
                      tabIndex={5}
                      className="text-sm font-semibold text-brand hover:text-brand-hover transition-colors"
                    >
                      Mit Passkey anmelden
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
